import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/*
 * «Tre vegar inn» – three doorways drawn as architecture, not photographed.
 *
 * Three equal arches stand on one shared floor (a double rule, like the one under the logotype).
 * Each arch is framed by a double hairline and holds a drawing of its idea: the rows of the
 * amphitheatre · a room seen from above with its door swinging open · one spotlight on a stage.
 * Below the drawing: the verb, the promise, the action. On hover/focus the door opens – ink rises
 * from the floor, the drawing turns to paper and only the point of light stays gold.
 */

const ease = [0.2, 0.8, 0.2, 1];
const VIEW = { once: true, margin: "-10% 0px" };

const Stroke = ({ d, i = 0, draw, w = 1.4, ...rest }) =>
  draw ? (
    <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round"
      initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={VIEW}
      transition={{ duration: 1.4, ease, delay: 0.15 + i * 0.1 }} {...rest} />
  ) : (
    <path d={d} fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" {...rest} />
  );

/** The one point of light – gold, and it grows a little when the door opens. */
const Light = ({ cx, cy, r = 4, draw, delay = 1.1 }) => (
  <g className="origin-center transition-transform duration-500 ease-house group-hover:scale-[1.35]" style={{ transformOrigin: `${cx}px ${cy}px`, transformBox: "view-box" }}>
    {draw ? (
      <motion.circle cx={cx} cy={cy} r={r} fill="hsl(var(--sol))" initial={{ scale: 0, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={VIEW} transition={{ duration: 0.6, ease, delay }} style={{ transformOrigin: `${cx}px ${cy}px`, transformBox: "view-box" }} />
    ) : (
      <circle cx={cx} cy={cy} r={r} fill="hsl(var(--sol))" />
    )}
  </g>
);

/** A closed ring drawn in one sweep (table tops, lamps). */
const Ring = ({ cx, cy, r, draw, w = 1.4, delay = 0.15 }) =>
  draw ? (
    <motion.circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeWidth={w} initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={VIEW} transition={{ duration: 1.3, ease, delay }} style={{ rotate: -90, transformOrigin: `${cx}px ${cy}px`, transformBox: "view-box" }} />
  ) : (
    <circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeWidth={w} />
  );

/** A seat: one person-sized dot of ink that pops into place. */
const Seat = ({ cx, cy, r = 4.2, i = 0, draw }) =>
  draw ? (
    <motion.circle cx={cx} cy={cy} r={r} fill="currentColor" initial={{ scale: 0, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={VIEW} transition={{ duration: 0.45, ease, delay: 0.5 + i * 0.05 }} style={{ transformOrigin: `${cx}px ${cy}px`, transformBox: "view-box" }} />
  ) : (
    <circle cx={cx} cy={cy} r={r} fill="currentColor" />
  );

/** Three rows of seats around the stage – and one of them is yours. */
const GlyphOppleve = ({ draw }) => (
  <svg viewBox="0 0 240 150" className="block h-auto w-full overflow-visible" aria-hidden="true" focusable="false">
    <Stroke draw={draw} i={0} w={1} d="M 160.3 110.3 A 58 58 0 0 0 79.7 110.3" />
    <Stroke draw={draw} i={1} w={1} d="M 188.0 102.6 A 84 84 0 0 0 52.0 102.6" />
    <Stroke draw={draw} i={2} w={1} d="M 217.1 100.4 A 110 110 0 0 0 22.9 100.4" />
    {/* the edge of the stage */}
    <Stroke draw={draw} i={3} d="M 92 128 H 148" />
    <Seat cx={155.7} cy={106.3} i={0} draw={draw} />
    <Seat cx={138.9} cy={97.2} i={1} draw={draw} />
    <Seat cx={120.0} cy={94.0} i={2} draw={draw} />
    <Seat cx={101.1} cy={97.2} i={3} draw={draw} />
    <Seat cx={84.3} cy={106.3} i={4} draw={draw} />
    <Seat cx={182.4} cy={95.8} i={5} draw={draw} />
    <Seat cx={164.5} cy={80.8} i={6} draw={draw} />
    <Seat cx={143.2} cy={71.3} i={7} draw={draw} />
    <Light cx={120.0} cy={68.0} r={5} draw={draw} delay={1.25} />
    <Seat cx={96.8} cy={71.3} i={8} draw={draw} />
    <Seat cx={75.5} cy={80.8} i={9} draw={draw} />
    <Seat cx={57.6} cy={95.8} i={10} draw={draw} />
    <Seat cx={211.2} cy={90.5} i={11} draw={draw} />
    <Seat cx={193.6} cy={70.3} i={12} draw={draw} />
    <Seat cx={171.6} cy={54.9} i={13} draw={draw} />
    <Seat cx={146.6} cy={45.3} i={14} draw={draw} />
    <Seat cx={120.0} cy={42.0} i={15} draw={draw} />
    <Seat cx={93.4} cy={45.3} i={16} draw={draw} />
    <Seat cx={68.4} cy={54.9} i={17} draw={draw} />
    <Seat cx={46.4} cy={70.3} i={18} draw={draw} />
    <Seat cx={28.8} cy={90.5} i={19} draw={draw} />
  </svg>
);

/** A round table laid for your party, seen from above. The chair nearest you is yours. */
const GlyphLeige = ({ draw }) => (
  <svg viewBox="0 0 240 150" className="block h-auto w-full overflow-visible" aria-hidden="true" focusable="false">
    <Ring cx={120} cy={72} r={30} draw={draw} />
    <Seat cx={168.0} cy={72.0} i={0} draw={draw} />
    <Seat cx={153.9} cy={38.1} i={1} draw={draw} />
    <Seat cx={120.0} cy={24.0} i={2} draw={draw} />
    <Seat cx={86.1} cy={38.1} i={3} draw={draw} />
    <Seat cx={72.0} cy={72.0} i={4} draw={draw} />
    <Seat cx={86.1} cy={105.9} i={5} draw={draw} />
    <Light cx={120.0} cy={120.0} r={5} draw={draw} delay={1.2} />
    <Seat cx={153.9} cy={105.9} i={7} draw={draw} />
  </svg>
);

/** On stage, in the light – with the first row waiting in front of you. */
const GlyphArrangere = ({ draw }) => (
  <svg viewBox="0 0 240 150" className="block h-auto w-full overflow-visible" aria-hidden="true" focusable="false">
    {/* the rig and the beam */}
    <Stroke draw={draw} i={0} d="M 108 10 H 132" />
    <Stroke draw={draw} i={1} w={1} d="M 120 10 L 80 104" strokeDasharray="1.5 6" />
    <Stroke draw={draw} i={2} w={1} d="M 120 10 L 160 104" strokeDasharray="1.5 6" />
    {/* the stage */}
    <Stroke draw={draw} i={3} d="M 52 104 H 188 V 113 H 52 Z" />
    <Light cx={120} cy={96} r={5} draw={draw} delay={1.2} />
    <Seat cx={60} cy={136} i={0} draw={draw} />
    <Seat cx={80} cy={136} i={1} draw={draw} />
    <Seat cx={100} cy={136} i={2} draw={draw} />
    <Seat cx={120} cy={136} i={3} draw={draw} />
    <Seat cx={140} cy={136} i={4} draw={draw} />
    <Seat cx={160} cy={136} i={5} draw={draw} />
    <Seat cx={180} cy={136} i={6} draw={draw} />
  </svg>
);

const GLYPHS = { oppleve: GlyphOppleve, leige: GlyphLeige, arrangere: GlyphArrangere };

export const IntentDoor = ({ item, index, draw }) => {
  const Glyph = GLYPHS[item.id] || GlyphOppleve;
  return (
    <Link
      to={item.to}
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-t-[999px] border border-b-0 border-foreground/30 bg-background text-foreground",
        "transition-[border-color,color] duration-300 ease-out",
        "hover:border-ink hover:text-paper dark:hover:border-paper dark:hover:text-ink",
        "focus-visible:outline-none focus-visible:border-ink focus-visible:text-paper dark:focus-visible:border-paper dark:focus-visible:text-ink"
      )}
      data-testid={`home-intent-${item.id}`}
      aria-label={`${item.eyebrow} – ${item.title}`}
    >
      {/* light falling in through the doorway */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-secondary to-transparent transition-opacity duration-500 group-hover:opacity-0" />
      {/* the door opens: ink rises from the floor */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 origin-bottom scale-y-0 bg-ink transition-transform duration-500 ease-house group-hover:scale-y-100 group-focus-visible:scale-y-100 dark:bg-paper" />
      {/* inner hairline – the second rule of the double frame */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-x-2.5 top-2.5 bottom-0 rounded-t-[999px] border border-b-0 border-current opacity-30 transition-opacity duration-300 group-hover:opacity-40" />

      {/* drawing */}
      <div className="relative px-[14%] pt-[22%]">
        <div className="transition-transform duration-500 ease-house group-hover:-translate-y-1">
          <Glyph draw={draw} />
        </div>
      </div>

      {/* words */}
      <div className="relative mt-auto px-7 pb-7 pt-9 sm:px-7 lg:px-9 lg:pb-9 lg:pt-10">
        <p className="t-mono flex items-center gap-x-2.5 text-muted-foreground transition-colors duration-300 group-hover:text-paper/60 dark:group-hover:text-ink/60">
          <span className="tabular">{String(index + 1).padStart(2, "0")}</span>
          <span className="h-px w-5 bg-current" aria-hidden="true" />
          <span>{["Første dør", "Andre dør", "Tredje dør"][index] || `Dør ${index + 1}`}</span>
        </p>
        <h3 className="mt-3 font-display text-[clamp(32px,2.6vw,46px)] font-semibold leading-[0.98] tracking-[-0.03em]">
          {item.eyebrow}<span className="text-sol">.</span>
        </h3>
        <p className="mt-3 max-w-[28ch] text-[14.5px] leading-relaxed text-muted-foreground transition-colors duration-300 group-hover:text-paper/72 dark:group-hover:text-ink/70">{item.text}</p>
        <p className="mt-6 inline-flex items-center gap-2.5 text-[14.5px] font-medium">
          <span className="u-hover decoration-current">{item.title}</span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-foreground transition-[background-color,color,transform] duration-300 group-hover:translate-x-0.5 group-hover:bg-sol group-hover:text-ink">
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </p>
      </div>
    </Link>
  );
};

/** Three doorways on one floor. Swipeable on small screens, a facade of three on larger ones. */
export const IntentDoors = ({ items = [], className }) => {
  const reduce = useReducedMotion();
  return (
    <div className={cn("relative", className)} data-testid="home-intent-doors">
      <ul className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 lg:gap-4">
        {items.map((it, i) => (
          <li key={it.id} className="w-[80vw] max-w-[360px] shrink-0 snap-center sm:w-auto sm:max-w-none sm:shrink sm:aspect-[3/4] lg:aspect-[4/5]">
            <IntentDoor item={it} index={i} draw={!reduce} />
          </li>
        ))}
      </ul>
      {/* the floor – a double rule, as under the logotype */}
      <div aria-hidden="true" className="border-t border-foreground/40" />
      <div aria-hidden="true" className="mt-1 border-t border-foreground/40" />
    </div>
  );
};
